import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    BookOpen,
    Play,
    Download,
    ArrowRight,
    CheckCircle,
    Lightbulb,
    Shield,
    Zap,
    Users,
    Globe,
    TrendingUp,
    Clock,
    Star
} from 'lucide-react';

const CleanBackground = () => {
    return (
        <div className="fixed inset-0 overflow-hidden pointer-events-none bg-gray-50">
            <div className="absolute inset-0 opacity-5">
                <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-gray-300 rounded-full transform rotate-12"></div>
                <div className="absolute top-1/3 right-1/3 w-40 h-40 bg-gray-300 rounded-full transform -rotate-6"></div>
                <div className="absolute bottom-1/3 left-1/2 w-36 h-36 bg-gray-300 rounded-full transform rotate-3"></div>
                <div className="absolute top-1/2 right-1/4 w-28 h-28 bg-gray-300 rounded-full transform -rotate-12"></div>
            </div>
        </div>
    );
};

export default function Learn() {
    const [selectedCategory, setSelectedCategory] = useState('all');

    const categories = [
        { id: 'all', label: 'All Topics', count: 24 },
        { id: 'basics', label: 'Getting Started', count: 8 },
        { id: 'investment', label: 'Investment Guide', count: 6 },
        { id: 'technology', label: 'Technology', count: 5 },
        { id: 'impact', label: 'Environmental Impact', count: 5 }
    ];

    const learningContent = [
        {
            id: 1,
            title: "How Clean Energy Investment Works",
            category: "basics",
            type: "video",
            duration: "5 min",
            difficulty: "Beginner",
            description: "Learn the fundamentals of investing in renewable energy projects and how your money creates impact.",
            thumbnail: "/assets/images/clean-energy-technology.jpg",
            rating: 4.8,
            views: 12500
        },
        {
            id: 2,
            title: "Understanding Tokenization in Energy",
            category: "technology",
            type: "article",
            duration: "8 min",
            difficulty: "Intermediate",
            description: "Discover how blockchain technology makes clean energy investment accessible and transparent.",
            thumbnail: "/assets/images/green-technology-innovation.jpg",
            rating: 4.6,
            views: 8900
        },
        {
            id: 3,
            title: "ROI Calculations for Solar Projects",
            category: "investment",
            type: "guide",
            duration: "12 min",
            difficulty: "Advanced",
            description: "Master the art of calculating returns on solar energy investments with real examples.",
            thumbnail: "/assets/images/solar-panel-farm.jpg",
            rating: 4.9,
            views: 15600
        },
        {
            id: 4,
            title: "Environmental Impact Measurement",
            category: "impact",
            type: "video",
            duration: "6 min",
            difficulty: "Beginner",
            description: "Understand how we measure and track the environmental benefits of clean energy projects.",
            thumbnail: "/assets/images/renewable-energy-infrastructure.jpg",
            rating: 4.7,
            views: 10200
        },
        {
            id: 5,
            title: "Risk Management in Green Investments",
            category: "investment",
            type: "article",
            duration: "10 min",
            difficulty: "Intermediate",
            description: "Learn how to assess and manage risks when investing in renewable energy projects.",
            thumbnail: "/assets/images/sustainable-investment.jpg",
            rating: 4.5,
            views: 7800
        },
        {
            id: 6,
            title: "Community Energy Projects Explained",
            category: "basics",
            type: "video",
            duration: "7 min",
            difficulty: "Beginner",
            description: "Explore how community-driven energy projects work and their benefits for local areas.",
            thumbnail: "/assets/images/clean-energy-team.jpg",
            rating: 4.8,
            views: 11200
        }
    ];

    const filteredContent = selectedCategory === 'all'
        ? learningContent
        : learningContent.filter(item => item.category === selectedCategory);

    const quickStartGuides = [
        {
            title: "First Investment Guide",
            description: "Step-by-step guide to making your first clean energy investment",
            icon: TrendingUp,
            color: "green"
        },
        {
            title: "Understanding Returns",
            description: "Learn how to calculate and understand your investment returns",
            icon: Zap,
            color: "blue"
        },
        {
            title: "Portfolio Diversification",
            description: "How to build a diversified clean energy investment portfolio",
            icon: Globe,
            color: "purple"
        }
    ];

    const faqItems = [
        {
            question: "What is the minimum investment amount?",
            answer: "You can start investing with as little as $100. This makes clean energy investment accessible to everyone while maintaining project viability."
        },
        {
            question: "How are returns calculated?",
            answer: "Returns are calculated based on the actual energy production and market rates. We provide transparent, real-time tracking of your investment performance."
        },
        {
            question: "What happens if a project fails?",
            answer: "We have comprehensive risk management and insurance policies. In rare cases of project failure, investors are protected through our risk mitigation strategies."
        },
        {
            question: "How do I track my environmental impact?",
            answer: "Our platform provides real-time dashboards showing CO₂ saved, energy produced, and other environmental metrics for all your investments."
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            <CleanBackground />

            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
                {/* Header Section */}
                <div className="text-center mb-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
                            Learn About <span className="text-green-600">Clean Energy</span>
                        </h1>
                        <p className="text-lg max-w-3xl mx-auto text-gray-600 mb-8">
                            Micro-explainer content that makes complexity invisible. Learn how clean energy
                            investment works, why tokenization matters, and get answers to common questions.
                        </p>
                    </motion.div>

                    {/* Category Filter */}
                    <motion.div
                        className="flex flex-wrap justify-center gap-4 mb-12"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.6 }}
                    >
                        {categories.map((category) => (
                            <button
                                key={category.id}
                                onClick={() => setSelectedCategory(category.id)}
                                className={`flex items-center space-x-2 px-6 py-3 rounded-lg transition-all duration-200 ${selectedCategory === category.id
                                    ? 'bg-green-600 text-white shadow-lg'
                                    : 'bg-white text-gray-700 hover:bg-gray-100 shadow-sm border border-gray-200'
                                    }`}
                            >
                                <span className="font-medium">{category.label}</span>
                                <span className="text-sm opacity-75">({category.count})</span>
                            </button>
                        ))}
                    </motion.div>
                </div>

                {/* Quick Start Guides */}
                <motion.div
                    className="mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.6 }}
                >
                    <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Quick Start Guides</h2>
                    <div className="grid md:grid-cols-3 gap-6">
                        {quickStartGuides.map((guide, index) => {
                            const Icon = guide.icon;
                            return (
                                <motion.div
                                    key={guide.title}
                                    className="bg-white p-6 rounded-2xl shadow-lg border border-gray-200"
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.6 + index * 0.1, duration: 0.6 }}
                                    whileHover={{ y: -5, scale: 1.02 }}
                                >
                                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${guide.color === 'green' ? 'bg-green-100' :
                                        guide.color === 'blue' ? 'bg-blue-100' : 'bg-purple-100'
                                        }`}>
                                        <Icon className={`w-6 h-6 ${guide.color === 'green' ? 'text-green-600' :
                                            guide.color === 'blue' ? 'text-blue-600' : 'text-purple-600'
                                            }`} />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-2">{guide.title}</h3>
                                    <p className="text-gray-600 mb-4">{guide.description}</p>
                                    <button className="flex items-center text-green-600 font-medium hover:text-green-700 transition-colors">
                                        Start Guide <ArrowRight className="w-4 h-4 ml-1" />
                                    </button>
                                </motion.div>
                            );
                        })}
                    </div>
                </motion.div>

                {/* Learning Content Grid */}
                <motion.div
                    className="mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8, duration: 0.6 }}
                >
                    <h2 className="text-2xl font-bold text-gray-900 mb-8">Learning Content</h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredContent.map((item, index) => (
                            <motion.div
                                key={item.id}
                                className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 1.0 + index * 0.1, duration: 0.6 }}
                                whileHover={{ y: -5, scale: 1.02 }}
                            >
                                {/* Thumbnail */}
                                <div className="h-48 bg-gradient-to-br from-green-100 to-blue-100 relative overflow-hidden">
                                    <img
                                        src={item.thumbnail}
                                        alt={item.title}
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute top-4 left-4">
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${item.difficulty === 'Beginner' ? 'bg-green-100 text-green-800' :
                                            item.difficulty === 'Intermediate' ? 'bg-yellow-100 text-yellow-800' :
                                                'bg-red-100 text-red-800'
                                            }`}>
                                            {item.difficulty}
                                        </span>
                                    </div>
                                    <div className="absolute top-4 right-4">
                                        <div className="flex items-center space-x-1 bg-black bg-opacity-50 text-white px-2 py-1 rounded">
                                            <Clock className="w-3 h-3" />
                                            <span className="text-xs">{item.duration}</span>
                                        </div>
                                    </div>
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="w-16 h-16 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
                                            {item.type === 'video' ? (
                                                <Play className="w-8 h-8 text-green-600 ml-1" />
                                            ) : (
                                                <BookOpen className="w-8 h-8 text-green-600" />
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="p-6">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm text-gray-500 capitalize">{item.type}</span>
                                        <div className="flex items-center space-x-1">
                                            <Star className="w-4 h-4 text-yellow-400 fill-current" />
                                            <span className="text-sm text-gray-600">{item.rating}</span>
                                        </div>
                                    </div>

                                    <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                                    <p className="text-gray-600 mb-4 text-sm leading-relaxed">{item.description}</p>

                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-gray-500">{item.views.toLocaleString()} views</span>
                                        <button className="flex items-center text-green-600 font-medium hover:text-green-700 transition-colors">
                                            {item.type === 'video' ? 'Watch' : 'Read'} <ArrowRight className="w-4 h-4 ml-1" />
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* FAQ Section */}
                <motion.div
                    className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.2, duration: 0.6 }}
                >
                    <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>
                    <div className="max-w-4xl mx-auto space-y-6">
                        {faqItems.map((faq, index) => (
                            <motion.div
                                key={index}
                                className="border border-gray-200 rounded-lg p-6"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 1.4 + index * 0.1, duration: 0.6 }}
                            >
                                <h3 className="text-lg font-bold text-gray-900 mb-3">{faq.question}</h3>
                                <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Call to Action */}
                <motion.div
                    className="text-center mt-16"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.6, duration: 0.6 }}
                >
                    <div className="bg-white p-8 md:p-12 rounded-2xl shadow-lg border border-gray-200 max-w-4xl mx-auto">
                        <h2 className="text-3xl font-bold mb-4 text-gray-900">
                            Ready to Start Learning?
                        </h2>
                        <p className="text-lg text-gray-600 mb-8">
                            Join thousands of investors who are building their knowledge and making informed decisions.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <motion.button
                                className="bg-green-600 text-white px-8 py-4 rounded-lg font-medium hover:bg-green-700 transition-colors"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                Start Learning
                            </motion.button>
                            <motion.button
                                className="bg-gray-100 text-gray-700 px-8 py-4 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                Download Resources
                            </motion.button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
}
